import { Component, computed, inject, input, output } from '@angular/core';
import { userDtos, webDtos, webEnums } from '@vectronic/openApi-customer';
import { Item, MobileService, roleRank } from '@vectronic/util';
import { DatatableComponent } from '../../components/datatable/datatable.component';
import { DatatableConfig } from '../../types/datatable-configuration';

@Component({
  selector: 'table-user-roles-table',
  templateUrl: './user-roles-table.component.html',
  styleUrls: ['./user-roles-table.component.scss'],
  imports: [DatatableComponent],
})
export class UserRolesTableComponent {
  item = input.required<Item>();
  ownRole = input.required<webEnums.Role>();
  shares = input.required<webDtos.Share[]>();
  currentUserEmail = input<string>();
  pendingRequestedShares = input<userDtos.SelfRequestedShare[]>([]);

  hideDeviceManagerRole = input(false);
  allowOwner = input.required<boolean>();
  numberOfOwners = computed(() => this.shares()?.filter(({ role }) => role === 'OWNER').length);

  stopSharing = output<webDtos.Share>();
  updateShare = output<webDtos.Share>();
  requestHigherRole = output<Exclude<webEnums.Role, 'OWNER'>>();
  withdrawRequestedShares = output<userDtos.SelfRequestedShare[]>();
  revokeAccess = output<void>();
  addShare = output();
  unsubscribeFromItem = output();

  mobileService = inject(MobileService);

  canStopSharing({ email, role }: webDtos.Share): boolean {
    const ownRole = this.ownRole();
    if (email === this.currentUserEmail()) return false;
    if (!ownRole || ownRole === 'READER') return false;
    if (role === 'OWNER') return false;
    if (ownRole === 'OWNER') return true;
    return roleRank(role) <= roleRank(ownRole);
  }

  canUnsubscribeFromItem(): boolean {
    if (this.item() === Item.ANIMAL || this.item() === Item.TRAP) return true;
    if (this.item() === Item.COLLAR) return this.ownRole() !== 'OWNER';
    return this.ownRole() === 'READER';
  }

  canUnsubscribeFromShare(share: webDtos.Share): boolean {
    if (share.email !== this.currentUserEmail()) return false;
    if (share.role === 'OWNER') return this.numberOfOwners() > 1;
    return this.canUnsubscribeFromItem();
  }

  canUseShareAction(share: webDtos.Share): boolean {
    if (share.email === this.currentUserEmail()) return this.canUnsubscribeFromShare(share);
    return this.canStopSharing(share);
  }

  handleShareAction(share: webDtos.Share): void {
    if (share.email === this.currentUserEmail()) this.unsubscribeFromItem.emit();
    else this.stopSharing.emit(share);
  }

  datatableConfig = computed<DatatableConfig<webDtos.Share>>(() => {
    let title: string;
    if (this.mobileService.isOn()) {
      title = '';
    } else if (this.ownRole() === 'READER') {
      title = 'Managers';
    } else {
      title = 'Users';
    }
    return {
      uniqueId: 'user_roles_inventa',
      title,
      columns: [
        {
          fieldName: 'email',
          columnId: 0,
          ellipsis: true,
        },
        {
          fieldName: 'role',
          component: {
            name: 'RoleSelectorComponent',
            input: {
              hideDeviceManager: () => this.hideDeviceManagerRole(),
              allowOwner: () => this.allowOwner(),
              numberOfOwners: () => this.numberOfOwners(),
              row: (row) => row,
              ownRole: () => this.ownRole(),
              currentUserEmail: () => this.currentUserEmail(),
              pendingRequestedShares: () => this.pendingRequestedShares(),
            },
            output: {
              requestHigherRole: (event) => this.requestHigherRole.emit(event),
              withdrawRequestedShares: (event) => this.withdrawRequestedShares.emit(event),
              revokeAccess: () => this.revokeAccess.emit(),
              updateShare: (event) => this.updateShare.emit(event),
            },
          },
          columnId: 1,
        },
      ],
      disableEditColumns: true,
      disableSort: true,
      disablePagination: true,
      disableDragColumns: true,
      headerIconButtons: [
        {
          icon: 'person_add',
          hoverText: 'Add a new user',
          onClick: () => this.addShare.emit(),
          doHide: this.ownRole() === 'READER',
        },
        {
          icon: 'help_outline',
          hoverText: 'Show permissions of roles',
          onClick: () => {
            const url = 'https://blog.inventa-vectronic.com/permissions/';
            if (window.desktop) window.desktop.app.openUrlInDefaultBrowserTab(url);
            else window.open(url, '_blank', 'noopener');
          },
        },
      ],
      rowActions: [
        {
          matIcon: 'close',
          label: 'Stop sharing',
          onClick: (row) => this.handleShareAction(row),
          doHide: (share) => !this.canUseShareAction(share),
          color: 'warn',
        },
      ],
    };
  });
}
